import { Box, Button, Grid, InputAdornment, Typography, IconButton } from '@mui/material';
import TextField from '@mui/material/TextField';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../../api/firebase';
import Swal from 'sweetalert2';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const SettingProfile = () => {
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordConfirmed, setIsPasswordConfirmed] = useState(false);

  // State for toggling password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const navigate = useNavigate();

  // Fetch admin data on component mount
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const username = localStorage.getItem('username');
        console.log('Username from localStorage:', username); // Debugging

        if (!username) {
          Swal.fire('Error!', 'Username not found in localStorage.', 'error');
          navigate("/");
          return;
        }

        const adminCollectionRef = collection(db, "admin");
        const querySnapshot = await getDocs(
          query(adminCollectionRef, where("username", "==", username))
        );

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          setPassword(docData.password);
          setUsername(docData.username);
        } else {
          Swal.fire('Error!', 'User not found.', 'error');
          navigate("/");
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
        Swal.fire('Error!', 'An error occurred while fetching data.', 'error');
        navigate("/");
      }
    };

    fetchAdminData();
  }, [navigate]);

  // Confirm current password
  const handleConfirmPassword = () => {
    if (confirmPassword === password) {
      setIsPasswordConfirmed(true);
      Swal.fire('Success!', 'Password confirmed. You can now update your profile.', 'success');
    } else {
      Swal.fire('Error!', 'Incorrect password. Please try again.', 'error');
    }
  };

  // Update profile (username and password)
  const handleUpdateProfile = async () => {
    if (!isPasswordConfirmed) {
      Swal.fire('Error!', 'Please confirm your password first.', 'error');
      return;
    }

    try {
      const usernameFromLocalStorage = localStorage.getItem('username');
      const adminCollectionRef = collection(db, "admin");
      const querySnapshot = await getDocs(
        query(adminCollectionRef, where("username", "==", usernameFromLocalStorage))
      );

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id; // Get the document ID
        const docRef = doc(db, "admin", docId);

        // Update both username and password fields
        await updateDoc(docRef, {
          username: username, // Update the username
          password: newPassword, // Update the password
        });

        // Update the username in localStorage
        localStorage.setItem('username', username);

        Swal.fire('Success!', 'Profile updated successfully.', 'success');
        setNewPassword('');
      } else {
        Swal.fire('Error!', 'User not found.', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire('Error!', 'An error occurred while updating the profile.', 'error');
    }
  };

  // Toggle password visibility
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Username"
            variant="outlined"
            size="small"
            sx={{ minWidth: '100%' }}
            value={username}
            onChange={(e) => setUsername(e.target.value)} // Allow editing of username
            disabled={!isPasswordConfirmed} // Enable only after password confirmation
          />
        </Grid>
        <Box height={20} />
        <Grid item xs={6}>
          <TextField
            label="Confirm Password"
            variant="outlined"
            size="small"
            sx={{ minWidth: '100%' }}
            type={showPassword ? 'text' : 'password'} // Toggle password visibility
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isPasswordConfirmed}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {!isPasswordConfirmed && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmPassword}
              sx={{ mt: 1 }}
            >
              Confirm Password
            </Button>
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="New Password"
            variant="outlined"
            size="small"
            sx={{ minWidth: '100%' }}
            type={showNewPassword ? 'text' : 'password'} // Toggle password visibility
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={!isPasswordConfirmed}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowNewPassword}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" align="center">
            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={!isPasswordConfirmed}
            >
              Update Profile
            </Button>
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
};

export default SettingProfile;